import { supabase } from '../lib/supabase';

export interface FileUploadResult {
  success: boolean;
  url?: string;
  error?: string;
}

export const uploadFile = async (
  file: File,
  bucketName: string,
  folder?: string
): Promise<FileUploadResult> => {
  try {
    console.log('=== File Upload Debug Info ===');
    
    // Get current user session for authorization
    const { data: { session } } = await supabase.auth.getSession();
    console.log('Session exists:', !!session);
    console.log('User exists:', !!session?.user);
    console.log('Access token exists:', !!session?.access_token);
    
    if (!session) {
      throw new Error('User not authenticated. Please log in again.');
    }
    
    if (!session.access_token) {
      throw new Error('Invalid session token. Please log in again.');
    }

    // Generate unique filename
    const timestamp = new Date().getTime();
    const fileExtension = file.name.split('.').pop();
    const fileName = folder 
      ? `${folder}/${timestamp}_${file.name}`
      : `${timestamp}_${file.name}`;
    
    console.log('Attempting to upload file:', fileName, 'Size:', file.size, 'bytes');
    console.log('File type:', file.type);
    
    // Upload file to Supabase Storage
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from(bucketName)
      .upload(fileName, file);

    if (uploadError) {
      console.error('Upload error details:', uploadError);
      
      // Provide specific error messages based on common issues
      if (uploadError.message.includes('row-level security')) {
        throw new Error(`Storage upload blocked by RLS policy. Please check storage policies in Supabase dashboard. Error: ${uploadError.message}`);
      } else if (uploadError.message.includes('bucket')) {
        throw new Error(`Storage bucket issue. Please verify "${bucketName}" bucket exists and has proper policies. Error: ${uploadError.message}`);
      } else if (uploadError.message.includes('permission')) {
        throw new Error(`Permission denied. Please check storage policies allow authenticated users to upload. Error: ${uploadError.message}`);
      } else {
        throw new Error(`Upload failed: ${uploadError.message}`);
      }
    }
    
    console.log('File uploaded successfully:', uploadData);

    // Get signed URL for the uploaded file
    const { data: urlData, error: urlError } = await supabase.storage
      .from(bucketName)
      .createSignedUrl(fileName, 60 * 60 * 24 * 365); // 1 year expiry

    if (urlError) {
      console.error('Signed URL error:', urlError);
      
      if (urlError.message.includes('row-level security')) {
        throw new Error(`Cannot create download URL due to RLS policy. Please check storage SELECT policies. Error: ${urlError.message}`);
      } else {
        throw new Error(`Failed to create download URL: ${urlError.message}`);
      }
    }
    
    console.log('Signed URL created:', urlData.signedUrl);
    console.log('=== End File Upload Debug ===');

    return {
      success: true,
      url: urlData.signedUrl
    };

  } catch (error) {
    console.error('Error uploading file:', error);
    console.error('Full error object:', {
      name: error instanceof Error ? error.name : 'Unknown',
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : 'No stack trace'
    });
    
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
};

export const deleteFile = async (
  bucketName: string,
  filePath: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase.storage
      .from(bucketName)
      .remove([filePath]);

    if (error) {
      throw new Error(`Failed to delete file: ${error.message}`);
    }

    return { success: true };
  } catch (error) {
    console.error('Error deleting file:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
};